/**
 * Shared field/payload shaping for the generic translatable resources API, used by
 * the standalone section page and by the subsections hosted inside the Shop page.
 *
 * A flat field id is "<resource id><separator><translation key>", so a single flat
 * list can hold the fields of many resources.
 */

/**
 * Field ids end up as DOM element ids, and the rich text editor looks its element up with
 * querySelectorAll('#' + id). A ':' there starts a CSS pseudo-selector, so the lookup throws
 * and the editor dies into its ErrorBoundary - which is why HTML sections could not use
 * richtext. Anything CSS-safe works: the left half is always a numeric resource id, and the
 * split is on the first occurrence.
 */
export const FIELD_ID_SEPARATOR = '__';

export const resourceIdOf = (shopifyId) => shopifyId.split('/').pop();

export const resourceName = (item) => item.display_name
  ? item.display_name
  : resourceIdOf(item.shopify_id);

/**
 * Editor shape per Shopify translatable content type. Anything unlisted falls back to a
 * single line text field, which is what SINGLE_LINE_TEXT_FIELD and URL want anyway.
 *
 * HTML defaults to a plain multiline box rather than a rich text editor, because HTML is
 * also what Shopify reports for templates carrying Liquid - a packing slip, an email
 * notification. A WYSIWYG rewrites the markup it is given and would eat the Liquid tags,
 * so the safe editor is the default and richtext is opted into per section, only where
 * the content is known to be prose. Same shape notifications already use for this.
 */
const FIELD_SHAPES = {
  HTML: { type: 'text', multiline: true, rows: 20 },
  MULTI_LINE_TEXT_FIELD: { type: 'text', multiline: true, rows: 5 },
};

const fieldShapeOf = (contentType, richText) => {
  if(contentType === 'HTML' && richText) {
    return { type: 'richtext', multiline: true, rows: 20 };
  }

  return FIELD_SHAPES[contentType] || { type: 'text', multiline: false, rows: 1 };
};

// maxLength caps the input, recommendedLength only counts - which of the two a key gets depends on
// whether Shopify enforces the limit on the translation or only on the source text.
const lengthLimitOf = (key, section) => {
  const max = section?.maxLengths?.[key];
  if(max) {
    return { maxLength: max };
  }

  const recommended = section?.recommendedLengths?.[key];

  return recommended ? { recommendedLength: recommended } : {};
};

/**
 * One field per translation. Labelled by the translation key the backend supplies -
 * prefixed with the resource name only where the caller asks for it, because the key
 * alone would not say which resource a row belongs to.
 *
 * `section` is the registry entry, or null where there is none to consult.
 */
export const buildTranslatableField = (item, translation, id, section) => ({
  id: id,
  ...fieldShapeOf(translation.type, section?.richTextFields === true),
  ...lengthLimitOf(translation.key, section),
  label: section?.prefixFieldLabels === true ? `${resourceName(item)} – ${translation.key}` : translation.key,
  source: translation.source,
  target: translation.target === null ? '' : translation.target,
  outdated: translation.outdated,
  error: translation.error,
  restore: translation.restore,
})

// label prefixing and richtext both come off the section entry, never inferred from the
// data - otherwise an unusual shop silently changes what the fields look like
export const buildTranslatableFields = (resources, section) => {
  const fields = [];

  resources.forEach(item => {
    item.translations.forEach(translation => {
      fields.push(buildTranslatableField(
        item,
        translation,
        `${resourceIdOf(item.shopify_id)}${FIELD_ID_SEPARATOR}${translation.key}`,
        section
      ))
    })
  })

  return fields;
}

/**
 * Resolves flat field ids back to the resources they came from
 * @return {Array} rows of {shopify_id, key, target}
 */
export const rowsFromFlatFields = (resources, fields) => {
  const rows = [];

  fields.forEach(field => {
    const separatorIndex = String(field.id).indexOf(FIELD_ID_SEPARATOR);
    if(separatorIndex === -1) {
      return;
    }
    const resourceId = field.id.substring(0, separatorIndex);
    const item = resources.find(x => resourceIdOf(x.shopify_id) === resourceId);
    if(item) {
      rows.push({
        shopify_id: item.shopify_id,
        key: field.id.substring(separatorIndex + FIELD_ID_SEPARATOR.length),
        target: field.target
      })
    }
  })

  return rows;
}

/**
 * Save payload for the rows whose target actually changed. An empty target deletes,
 * and a translation without a digest is dropped by the route, so it is left out here
 * @return {Object} GID => {translations: [{key, target, digest}]}
 */
export const buildTranslatableSaveMap = (resources, rows) => {
  const map = {};

  rows.forEach(row => {
    const item = resources.find(x => x.shopify_id === row.shopify_id);
    const translation = item && item.translations.find(x => x.key === row.key);
    if(!translation || !translation.digest) {
      return;
    }

    const target = row.target ? row.target : '';
    if(target === (translation.target === null ? '' : translation.target)) {
      return;
    }

    if(!map[row.shopify_id]) {
      map[row.shopify_id] = { translations: [] };
    }
    map[row.shopify_id].translations.push({
      key: row.key,
      target: target,
      digest: translation.digest,
    })
  })

  return map;
}

export const translatableProgress = (resources) => {
  const translations = resources.reduce((all, item) => all.concat(item.translations), []);
  if(translations.length === 0) {
    return 0;
  }
  return (translations.filter(x => x.target !== null && x.target !== '').length / translations.length) * 100;
}
